import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DeadlineCotractApi } from '@api/deadlineContract.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { MaintenanceTypeService } from '@core/components/inputs/maintenance-type/maintenance-type.service';
import { AddressCoords, Maintenance, Problem } from '@core/interfaces';
import { DeadlineContract } from '@core/interfaces/deadlines-contracts';
import { TimeValidation } from '@core/validators/time-validation';
import { ConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import brAddressFormatter from 'br-address-formatter';
import { merge } from 'lodash';
import moment from 'moment';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { MaintenanceType } from '../../../../../../core/interfaces/maintenance-type';
import english from '../../../../i18n/en.json';
import spanish from '../../../../i18n/es.json';
import portuguese from '../../../../i18n/pt.json';
import { MaintenanceDialogData } from '../../dialog-maintenances.component';

@Component({
  selector: 'tab-maintenances',
  templateUrl: './tab-maintenance.component.html',
  styleUrls: ['./tab-maintenance.component.scss']
})
export class TabMaintenanceComponent implements OnInit, OnDestroy {

  public isViewMode = false;
  public isClosedMaintenance = false;
  public problems: Problem[] = [];
  public maintenancesForm: FormGroup;
  public addressCoords: AddressCoords = null;
  public maintenanceTypes: MaintenanceType[] = [];
  public validStatusToEditMaintenance = ['new', 'requested', 'onNegotiation'];

  @Output() getMaintenanceForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @Output() isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() isSavingMaintenance: boolean;

  public whereSegments = { archived: false };
  public whereProblems = { archived: false };
  public maintenance: Maintenance;
  public shouldShowScheduleSuggestion: boolean;

  public hasLabelViewPermission = this.authenticationService.hasPermission('labels-list');

  private readonly destroyed = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MaintenanceDialogData,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private maintenanceTypeService: MaintenanceTypeService,
    private formBuilder: FormBuilder,
    public matDialog: MatDialog,
    private readonly authenticationService: AuthenticationService,
    private readonly deadlineContractApi: DeadlineCotractApi,
    private readonly translateService: TranslateService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
    this.maintenance = data.maintenance;
    this.isViewMode = data.isViewMode;
  }

  ngOnInit(): void {
    if (this.maintenance.scheduled && this.maintenance.scheduled.date) {
      this.maintenance.scheduledAt = this.maintenance.scheduled.date;
    }
    if (this.maintenance.scheduled && this.maintenance.scheduled.time) {
      this.maintenance.scheduledAt += `T${this.maintenance.scheduled.time}`;
    }

    this.isClosedMaintenance = this.maintenance.status === 'done' || this.maintenance.status === 'canceled';
    this.maintenancesForm = this.formBuilder.group({

      id: this.maintenance.id ? this.maintenance.id : null,
      number: new FormControl({ value: this.maintenance.number, disabled: true }),
      segment: new FormControl(this.maintenance.segment, Validators.required),
      problemId: new FormControl(this.maintenance.problem && this.maintenance.problem.id),
      typeId: new FormControl((this.maintenance.maintenanceType && this.maintenance.maintenanceType.id), Validators.required),
      message: new FormControl(this.maintenance.message, [Validators.required, Validators.maxLength(2000)]),
      status: this.maintenance.status || 'new',
      weight: new FormControl(this.maintenance.weight || 1, [Validators.required, Validators.min(1), Validators.max(5)]),
      locationId: [(this.maintenance.location && this.maintenance.location.id), Validators.required],
      address: this.maintenance.address ? this.formBuilder.group({
        ...this.maintenance.address,
        formattedAddress: brAddressFormatter(this.maintenance.address)
      }, Validators.required) : this.formBuilder.group({ formattedAddress: null }),
      openedDate: {
        value: this.maintenance.openedAt ? moment(this.maintenance.openedAt).toDate() : null,
        disabled: true
      },
      openedTime: {
        value: this.maintenance.openedAt ? moment(this.maintenance.openedAt).format('HH:mm') : null,
        disabled: true
      },
      scheduleSuggestions: this.maintenance.scheduleSuggestions.length > 0 ? new FormArray(
        this.maintenance.scheduleSuggestions.map((scheduleSuggestion) => new FormControl(scheduleSuggestion))
      ) : new FormArray([]),
      scheduleSuggestionsStatus: this.maintenance.scheduleSuggestionsStatus ? this.maintenance.scheduleSuggestionsStatus : null,
      companyScheduleSuggestion: this.maintenance.companyScheduleSuggestion ? this.maintenance.companyScheduleSuggestion : null,
      scheduleSuggestionsWasChanged: false,
    });

    this.maintenancesForm.get('segment').valueChanges.subscribe(async () => {
      await this.setMaintenacesType();
    });


    if (this.maintenance.deadlineContract) {
      const responseDeadlineViolationReason = this.maintenance.deadlineContract.responseDeadlineViolationReason && this.maintenance.deadlineContract.responseDeadlineViolationReason.deadlineViolationReason;
      const solutionDeadlineViolationReason = this.maintenance.deadlineContract.solutionDeadlineViolationReason && this.maintenance.deadlineContract.solutionDeadlineViolationReason.deadlineViolationReason;
      this.maintenancesForm.addControl('responseDeadlineViolationReason', new FormControl(responseDeadlineViolationReason));
      this.maintenancesForm.addControl('solutionDeadlineViolationReason', new FormControl(solutionDeadlineViolationReason));
    }

    if (this.maintenance.problem && this.maintenance.problem.id) {
      this.problems = [this.maintenance.problem];
      this.maintenancesForm.get('problemId').setValue(this.maintenance.problem.id);
    }

    if (
      !this.maintenancesForm.get('address').get('formattedAddress').value && this.maintenance.location &&
      this.maintenance.location.address && this.maintenance.location.address.formattedAddress
    ) {
      this.maintenancesForm.get('address').get('formattedAddress').setValue(this.maintenance.location.address.formattedAddress);
    }

    this.maintenancesForm.addControl('requesterId', new FormControl({
      value: this.maintenance.requester && this.maintenance.requester.id,
      disabled: true
    }, Validators.required));

    this.maintenancesForm.addControl('assignee', new FormControl(
      this.maintenance.assignee
    ));

    this.maintenancesForm.addControl('company', new FormControl(
      this.maintenance.company
    ));


    this.maintenancesForm.addControl('deadlineContract', new FormControl(
      this.maintenance.deadlineContract && this.maintenance.deadlineContract.originalContract || null
    ));

    if (!this.validStatusToEditMaintenance.includes(this.maintenance.status)) {
      this.maintenancesForm.get('deadlineContract').disable();
    }


    this.subscribeToDeadlineContract();

    this.maintenancesForm.addControl('statusDescription', new FormControl({
      value: this.maintenance.statusDescription,
      disabled: this.maintenance.company && this.maintenance.company.fieldAccountId ? true : false
    }, Validators.required));

    if (this.isViewMode) {
      this.maintenancesForm.disable();
    }

    this.maintenancesForm.valueChanges.subscribe(() => {
      this.getMaintenanceForm.emit(this.maintenancesForm);
    });

    this.maintenancesForm.get('status').valueChanges.subscribe((value) => {

      this.isClosedMaintenance = value === 'done' || value === 'canceled';
      const companyUsesFieldControl = this.maintenance.company && this.maintenance.company.fieldAccountId;

      if (this.isClosedMaintenance) {
        this.maintenancesForm.addControl('closedDate', new FormControl({
          value: this.maintenance.closedAt ? moment(this.maintenance.closedAt).toDate() : null,
          disabled: companyUsesFieldControl
        }));
        this.maintenancesForm.addControl('closedTime', new FormControl({
          value: this.maintenance.closedAt ? moment(this.maintenance.closedAt).format('HH:mm') : null,
          disabled: companyUsesFieldControl
        }));
        this.maintenancesForm.addControl('startDate', new FormControl({
          value: this.maintenance.firstVisitStartedAt ? moment(this.maintenance.firstVisitStartedAt).toDate() : null,
          disabled: companyUsesFieldControl
        }));
        this.maintenancesForm.addControl('startTime', new FormControl({
          value: this.maintenance.firstVisitStartedAt ? moment(this.maintenance.firstVisitStartedAt).format('HH:mm') : null,
          disabled: companyUsesFieldControl
        }));
        this.maintenancesForm.addControl('statusDescription', new FormControl({
          value: this.maintenance.statusDescription,
          disabled: companyUsesFieldControl
        }));
        this.maintenancesForm.setValidators(TimeValidation.validate.bind(this));
      }

      if (value !== 'new' && value !== 'pending') {
        this.maintenancesForm.addControl('scheduledDate', new FormControl({
          value: (this.maintenance.scheduled && this.maintenance.scheduled.date) ? moment(this.maintenance.scheduledAt).toDate() : null,
          disabled: companyUsesFieldControl
        }));
        this.maintenancesForm.addControl('scheduledTime', new FormControl({
          value: (this.maintenance.scheduled && this.maintenance.scheduled.time) ? moment(this.maintenance.scheduledAt).format('HH:mm') : null,
          disabled: companyUsesFieldControl
        }));
        this.maintenancesForm.setValidators(TimeValidation.validate.bind(this));
      }

      if (value === 'new' || value === 'pending') {
        this.maintenancesForm.removeControl('scheduledDate');
        this.maintenancesForm.removeControl('scheduledTime');
        this.maintenancesForm.removeControl('closedDate');
        this.maintenancesForm.removeControl('closedTime');
        this.maintenancesForm.removeControl('startDate');
        this.maintenancesForm.removeControl('startTime');
        this.maintenancesForm.removeControl('statusDescription');
        this.maintenancesForm.setValidators(null);
      }
    });

    this.shouldShowScheduleSuggestion = this.shouldShowScheduleSuggestionElement();

    this.isLoading.pipe(
      takeUntil(this.destroyed)
    ).subscribe(() => {
      this.shouldShowScheduleSuggestion = this.shouldShowScheduleSuggestionElement();
    });

    this.getMaintenanceCoords();
  }

  public ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  public async setMaintenacesType(): Promise<void> {
    const segment = this.maintenancesForm.get('segment').value;
    const segmentHasMaintenanceTypesSeted = segment.maintenanceTypes && segment.maintenanceTypes.length > 0;

    await this.maintenanceTypeService.listMaintenanceTypes({
      where: {
        archived: false,
        segmentId: segmentHasMaintenanceTypesSeted ? this.maintenancesForm.get('segment').value.id : null,
      }
    }).then((maintenanceTypes) => {
      this.maintenanceTypes = maintenanceTypes;

      const currentMaintenanceTypeIsOnList = this.maintenanceTypes.some((maintenanceType) => {
        return maintenanceType.id === this.maintenance.maintenanceType.id;
      });

      if (!currentMaintenanceTypeIsOnList) {
        this.maintenanceTypes.push(this.maintenance.maintenanceType);
      }

      this.maintenancesForm.updateValueAndValidity();
    });
  }

  shouldShowScheduleSuggestionElement(): boolean {
    const maintenanceStatus = this.maintenancesForm.get('status');
    const scheduleSuggestionsStatus = this.maintenancesForm.get('scheduleSuggestionsStatus');

    const isValidStatusesToShow = Boolean(
      maintenanceStatus.value === 'new' ||
      maintenanceStatus.value === 'requested' ||
      maintenanceStatus.value === 'onNegotiation'
    );
    const isSuggestionStopped = ((scheduleSuggestionsStatus.value && scheduleSuggestionsStatus.value === 'suggestionStopped'));

    return (isValidStatusesToShow && !isSuggestionStopped);
  }

  getWhereSegments(): object {
    if (this.maintenance && this.maintenance.id) {
      return {};
    } else {
      return { archived: false };
    }
  }

  private getMaintenanceCoords(): void {
    if (this.maintenance) {
      const maintenanceHasAddressCoords = this.maintenance.address && this.maintenance.address.coords.latitude && this.maintenance.address.coords.longitude;
      this.addressCoords = maintenanceHasAddressCoords
        ? this.maintenance.address.coords
        : this.maintenance.location.address.coords;
    }
  }

  private subscribeToDeadlineContract(): void {
      merge(
        this.maintenancesForm.get('typeId').valueChanges,
        this.maintenancesForm.get('segment').valueChanges
      )
      .pipe(
        takeUntil(this.destroyed),
        filter(() => {
          const hasSegment = Boolean(this.maintenancesForm.get('segment').value);
          const hasMaintenanceType = Boolean(this.maintenancesForm.get('typeId').value);

          const typeChanged = this.maintenancesForm.get('typeId').value !== this.data.maintenance.maintenanceType.id;
          const segmentChanged = this.maintenancesForm.get('segment').value.id !== this.data.maintenance.segment.id;

          const typeOrSegmentChanged = typeChanged || segmentChanged;
          return (
            hasSegment && hasMaintenanceType && typeOrSegmentChanged
          );
        }),
        switchMap(() => {
          return this.deadlineContractApi.getDeadlineContractByConfig({
            maintenanceTypeId: this.maintenancesForm.get('typeId').value,
            segmentId: this.maintenancesForm.get('segment').value.id,
            locationId: this.maintenancesForm.get('locationId').value
          });
        })
      )
      .subscribe(async (receivedDeadlineContract: DeadlineContract | null) => {
        if (!receivedDeadlineContract) {
          return;
        }

        const currentDeadlineContract = this.maintenancesForm.get('deadlineContract').value;

        if (!currentDeadlineContract) {
          this.maintenancesForm.get('deadlineContract').setValue(receivedDeadlineContract);

          return;
        }

        const receivedDeadlineContractIsDifferentThanCurrent = currentDeadlineContract.id !== receivedDeadlineContract.id;

        if (receivedDeadlineContractIsDifferentThanCurrent) {
          const result = await this.matDialog.open(ConfirmDialogComponent, {
            data: {
              title: this.translateService.instant('MAINTENANCES.DEADLINE_CONTRACT_DIALOG.TITLE'),
              message: this.translateService.instant('MAINTENANCES.DEADLINE_CONTRACT_DIALOG.MESSAGE'),
              okText: this.translateService.instant('MAINTENANCES.DEADLINE_CONTRACT_DIALOG.OK_TEXT'),
              cancelText: this.translateService.instant('MAINTENANCES.DEADLINE_CONTRACT_DIALOG.CANCEL_TEXT')
            }
          })
            .afterClosed()
            .toPromise();

          if (!result || !result.confirmed) {
            return;
          }

          this.maintenancesForm.get('deadlineContract').setValue(receivedDeadlineContract);
          return;
        }
      });
    }
}
